import { Modal, Text, UploadDragger, Group, TextInput, notification } from "components/common"
import { Form } from "antd"
import { useWatch } from "antd/lib/form/Form"
import { forwardRef, useEffect } from "react"
import { t } from "i18next"
import { useGetDataFromImageMutation } from "features/leads/api"
import { useAddClientIBANMutation, useCreateVIBANForContractMutation } from "features/shared/api"
import { localisedValue } from "utils"
import { schemaRules } from "features/shared/utils"
import dayjs from "dayjs"
import { YYYY_MM_DD_FORMAT } from "constants"
import clientIbanFormValidations from "./clientIbanFormValidations"

const ClientIbanForm = forwardRef(({ contractId, clientId, clientIban, paymentCompleted }, ref) => {
  const [form] = Form.useForm()
  const attachment = useWatch("attachment", form)
  const [getDataFromImage, { data, isLoading, error }] = useGetDataFromImageMutation()
  const [addClientIBAN, { isLoading: uploadingIBAN, error: clientIbanError, reset: resetIbanEror }] =
    useAddClientIBANMutation()
  const [creatingVIBAN, { data: vIBAN, isLoading: fetchingVIBAN, error: vIbanError, reset: resetVibanError }] =
    useCreateVIBANForContractMutation()

  const rules = schemaRules(clientIbanFormValidations())

  const resetForm = () => {
    form.resetFields()
    ref?.current?.setVisible(false)
    resetIbanEror()
    resetVibanError()
  }
  const onSubmit = values => {
    const payload = {
      iban: values?.iban,
      accountTitle: values?.accountTitle,
      ibanCertificateDate: dayjs(data?.imageData?.date ?? undefined).format(YYYY_MM_DD_FORMAT),
      ibanCertificateImageDataId: data?.imageData?.id,
    }
    addClientIBAN({ body: payload, clientId: clientId })
      ?.unwrap()
      ?.then(() => {
        if (paymentCompleted) {
          creatingVIBAN({
            contractId: contractId,
            clientIban: values?.iban,
          })
            ?.unwrap()
            ?.then(data => {
              notification.success({ message: data?.message })
              resetForm()
            })
            ?.catch(error => notification.error(error))
        } else {
          resetForm()
        }
      })
      ?.catch(error => notification.error(error))
  }
  useEffect(() => {
    resetIbanEror()
    attachment?.length &&
      getDataFromImage({
        attachmentTypeId: 8,
        name: attachment?.[0]?.name,
        file: attachment?.[0]?.file,
      })
        ?.unwrap()
        .then(resp => {
          form.setFieldsValue({
            iban: resp?.imageData?.iban,
            accountTitle: resp?.imageData?.personName,
            bank: localisedValue({ object: resp?.imageData?.Bank, key: "title" }),
          })
        })
        ?.catch(error => notification.error(error))
  }, [attachment?.length])

  return (
    <Modal
      title={t("Customer Bank Information")}
      ref={ref}
      destroyOnClose
      okText={t("Submit")}
      onOk={form.submit}
      cancelText={t("Cancel")}
      disableOkButton={isLoading || fetchingVIBAN || uploadingIBAN || !attachment?.length}
      loading={isLoading || fetchingVIBAN || uploadingIBAN}
      onCancel={resetForm}
      footerAlert={error || clientIbanError || vIbanError}
    >
      <Form layout="vertical" form={form} onFinish={onSubmit} initialValues={{ attachment: [] }}>
        <Text type="secondary" className="mbe-16">
          {t(
            "Please provide the client's IBAN detalls. This is needed to generate a Virtual IBAN for payments and to handle refunds. You can upload the certificate or enter the 24-digit IBAN below."
          )}
        </Text>
        <Group template="repeat(2,1fr)" gap="0 24px">
          <Form.Item name={"attachment"} label={t("IBAN Certificate")} required>
            <UploadDragger
              name="attachment"
              files={attachment}
              maxCount={1}
              setFiles={cb => {
                form.setFieldsValue({ attachment: cb(attachment) })
              }}
              disabled={isLoading}
            />
          </Form.Item>
          {attachment?.length && data?.imageData && !error ? (
            <>
              <Form.Item name="iban" label={t("IBAN")} rules={rules} required>
                <TextInput placeholder={t("Enter IBAN")} />
              </Form.Item>
              <Form.Item name="accountTitle" label={t("Account Title")} rules={rules} required>
                <TextInput placeholder={t("Enter Account Title")} />
              </Form.Item>
              <Form.Item name="bank" label={t("Bank")} rules={rules} required>
                <TextInput placeholder={t("Enter Bank Name")} />
              </Form.Item>
            </>
          ) : null}
        </Group>
      </Form>
    </Modal>
  )
})
export default ClientIbanForm
